import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { MapPinIcon } from 'lucide-react';
import { MockupTag } from './MockupTag';
import { TextLink } from './TextLink';
import { destinationPath } from '../../utils/paths';
import type { Destination, Season } from '../../types/content';
interface LocationPickerProps {
  locations: Destination[];
  mode?: 'campus' | 'activities';
  label?: string;
  season?: Season;
}
export function LocationPicker({
  locations,
  mode = 'campus',
  label = 'Choose a location',
  season
}: LocationPickerProps) {
  const [selected, setSelected] = useState(locations[0]?.slug ?? '');
  const current = locations.find(location => location.slug === selected) ?? locations[0];
  if (!current) return null;
  return <div className="grid gap-8 lg:grid-cols-12" data-id="element-783" data-mp="11bt3jjzsbop2">
      <div className="lg:col-span-5" data-id="element-784" data-mp="11cl1h1gutyii">
        <p id="location-picker-label" className="mb-3 type-form-label text-ink" data-id="element-785" data-mp="119gmd91upid2">
          {label}
        </p>
        <div role="radiogroup" aria-labelledby="location-picker-label" className="grid grid-cols-2 gap-2 sm:grid-cols-3" data-id="element-786" data-mp="2l437ta3jvhrm">
          {locations.map(location => {
          const active = location.slug === current.slug;
          return <button key={location.slug} type="button" role="radio" aria-checked={active} onClick={() => setSelected(location.slug)} className={`rounded-xl border-2 bg-white px-4 py-3 text-left transition-colors duration-150 ${active ? 'border-brand' : 'border-line hover:border-muted'}`} data-id="element-787" data-mp="2v1hn36dqm0ic">
                <span className="block whitespace-nowrap type-list font-bold text-ink" data-id="element-788" data-mp="3dv4qhl6gbvfw">{location.name}</span>
                <span className="block truncate type-fine text-muted" data-id="element-789" data-mp="3mtgquex4e7lj">{location.country}</span>
              </button>;
        })}
        </div>
      </div>
      <div className="lg:col-span-7" data-id="element-790" data-mp="3rstbct0ir2ky">
        <AnimatePresence mode="wait" initial={false} data-id="element-791" data-mp="1rq0see5wvm04">
          <motion.div key={current.slug} initial={{
          opacity: 0,
          y: 6
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -6
        }} transition={{
          duration: 0.18,
          ease: [0.23, 1, 0.32, 1]
        }} className="grid overflow-hidden rounded-2xl border border-line bg-white md:grid-cols-2" data-id="element-792" data-mp="1rqtuh1txdnbx">
            <div className="relative" data-id="element-793" data-mp="2bdi0oan7zip6">
              <img src={current.image} alt={`City view in ${current.name}`} className="h-56 w-full object-cover md:h-full" data-id="element-794" data-mp="2bcgcr5l3v1q1" />
              <MockupTag note="Placeholder image" className="absolute left-3 top-3" data-id="element-795" data-mp="26hj80auuzeiy" />
            </div>
            <div className="flex flex-col p-6" data-id="element-796" data-mp="2ffv4gtiep6jb">
              <h3 className="type-card-title text-ink" data-id="element-797" data-mp="2fh68mufrkgay">{current.name}</h3>
              <p className="mt-1 type-small text-muted" data-id="element-798" data-mp="q0ez7p7ls4yp">{current.country}</p>
              {mode === 'campus' ? <div className="mt-5 space-y-4" data-id="element-799" data-mp="l2wvqeccuhy3">
                  <MockupTag note="Sample address" data-id="element-800" data-mp="l4812ilg7xrb" />
                  {current.campuses.map(campus => <address key={campus.name} className="flex gap-3 not-italic" data-id="element-801" data-mp="31oa95zmmqbaz">
                      <MapPinIcon className="mt-0.5 h-5 w-5 shrink-0 text-brand" aria-hidden="true" data-id="element-802" data-mp="31q4lipju4v3g" />
                      <span className="type-card-desc" data-id="element-803" data-mp="2nv2ica6dhjuc">
                        <span className="block font-semibold text-ink" data-id="element-804" data-mp="2rx5xb8s936kd">{campus.name}</span>
                        {campus.lines.map(line => <span key={line} className="block" data-id="element-805" data-mp="2e1233fhdmy7w">
                            {line}
                          </span>)}
                      </span>
                    </address>)}
                </div> : <ul className="mt-5 space-y-3" data-id="element-806" data-mp="28zwarirmpcyw">
                  {current.activities.slice(0, 4).map(activity => <li key={activity.name} className="type-card-desc" data-id="element-807" data-mp="34tp2qf89rsd">
                      <span className="block font-semibold text-ink" data-id="element-808" data-mp="77gj8xzdfosq">{activity.name}</span>
                      {activity.text}
                    </li>)}
                </ul>}
              <div className="mt-auto flex flex-col gap-2 pt-6" data-id="element-809" data-mp="39oohqtm7b5oq">
                <TextLink to={destinationPath(current, season)} data-id="element-810" data-mp="34nsemi9obnkd">Open the {current.name} page</TextLink>
                {current.priceList ? <TextLink to={`/price-list/${current.priceList}`} data-id="element-811" data-mp="2zs38tx729afm">{current.name} price list</TextLink> : null}
              </div>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </div>;
}